/**
 * @module app.shared
 */

import { ApiDate, ApiDateTime, ApiSingle, ApiDouble, ApiDecimal } from './essette-api';
import { IModel } from 'app/shared/models/essette-interfaces';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';

export function fromApiDate(apiDate?: ApiDate | null): DateValue | null {
    if(!apiDate) { return null; }
    return new DateValue(apiDate);
}

export function toApiDate(dateValue?: DateValue | null): ApiDate | null {
    if(!dateValue) { return null; }
    return dateValue.toJSON();
}

export function fromApiDateTime(apiDateTime?: ApiDateTime | null): DateTimeValue | null {
    if(!apiDateTime) { return null; }
    return new DateTimeValue(apiDateTime);
}

export function toApiDateTime(dateTimeValue?: DateTimeValue | null): ApiDateTime | null {
    if(!dateTimeValue) { return null; }
    return dateTimeValue.toJSON();
}

export function fromApiSingle(apiSingle?: ApiSingle | null): number | null {
    if(apiSingle === undefined || apiSingle === null || <string>apiSingle === '') { return null; }
    if(apiSingle === 'NaN') { return Number.NaN; }
    if(apiSingle === 'Infinity') { return Number.POSITIVE_INFINITY; }
    if(apiSingle === '-Infinity') { return Number.NEGATIVE_INFINITY; }
    if(typeof apiSingle === 'number') { return apiSingle; }
    return parseFloat(`${apiSingle}`);
}

export function toApiSingle(value?: number | null): ApiSingle | null {
    if(value === undefined || value === null) { return null; }
    if(Number.isNaN(value)) { return 'NaN'; }
    if(Number.isFinite(value)) { return value; }
    if(value < 0.0) { return '-Infinity'; }
    return 'Infinity';
}

export function fromApiDouble(apiDouble?: ApiDouble | null): number | null {
    if(apiDouble === undefined || apiDouble === null || <string>apiDouble === '') { return null; }
    if(apiDouble === 'NaN') { return Number.NaN; }
    if(apiDouble === 'Infinity') { return Number.POSITIVE_INFINITY; }
    if(apiDouble === '-Infinity') { return Number.NEGATIVE_INFINITY; }
    if(typeof apiDouble === 'number') { return apiDouble; }
    return parseFloat(`${apiDouble}`);
}

export function toApiDouble(value?: number | null): ApiDouble | null {
    if(value === undefined || value === null) { return null; }
    if(Number.isNaN(value)) { return 'NaN'; }
    if(Number.isFinite(value)) { return value; }
    if(value < 0.0) { return '-Infinity'; }
    return 'Infinity';
}

export function fromApiDecimal(apiDecimal?: ApiDecimal | null): number | null {
    if(apiDecimal === undefined || apiDecimal === null || apiDecimal === '') { return null; }
    return parseFloat(`${apiDecimal}`);
}

export function toApiDecimal(value?: number | string | null): ApiDecimal | null {
    if(value === undefined || value === null) { return null; }
    if(typeof value === 'number') {
        return value.toFixed()
    }
    // TODO: does exponential notation break API?
    return `${value}`;
}

/**
 * Creates a reference to the specified `source` model, which only includes `{id: source.id}` merged with
 * any partial information in `additional`.
 *
 * * If `source` is `null` the result is `null`.
 * * If `source` is `undefined` the result is `undefined`.
 * * If `source.id` is `null` or `undefined` the result is `undefined`.
 *
 * @param source the source model to create a reference to
 * @param additional any additional properties to attach to the returned reference
 */
export function getReference<T extends IModel>(source: T | null | undefined, additional?: Partial<T>): T | null | undefined {
    if(source && source.id) {
        if(source.$state && source.$state === 'inactive') { return undefined; }
        return ({
            id: (source && source.id) || undefined,
            ... (additional as object)
        }) as any;
    }
    if(source === null)
        return null;

    return undefined;
}

/**
 * Creates an array of references to the specified models in `sources`, which only includes `{id: sources[n].id}`
 * merged with any partial information created by the `additionalFactory` callback function.
 *
 * This method is the `Array` analog to `getReference(...)`, and uses it internally.
 *
 * * If `sources` is `null` or `undefined` the result is `undefined`.
 * * If `sources` is empty, an empty `Array` is returned.
 *
 * @param sources the source models to create an array of references to
 * @param additionalFactory a function that returns additional information for each item in `sources` to attach to the returned reference
 */
export function getReferences<T extends IModel>(sources: T[] | null | undefined, additionalFactory?: (source: T) => Partial<T>): T[] | undefined {
    if(sources) {
        return sources
            .map(source => getReference(source, additionalFactory && additionalFactory(source)))
            .filter((source): source is T => !!source);
    }

    return undefined;
}

/**
 * Build a snapshot of ID values used to compare two sets of data referentially.
 *
 * Useful for change detection between subsequent queries.
 * @param items an array of models with `id` properties
 */
export function buildSnapshot(items: IModel[]): string {
    return JSON.stringify(items.map(item => item.id).filter(id => !!id));
}
